import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Flame, Users, MapPin, Music, MessageSquare, UtensilsCrossed, Share2, Home as HomeIcon, Globe } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] w-full flex items-center justify-center overflow-hidden" style={{ maxWidth: '100vw' }}>
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1653169774094-73c7caeaaadc?w=2400&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center 70%',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '100%',
            left: 0,
            right: 0
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/40 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <Flame className="w-20 h-20 text-amber-500" strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Viking Funeral Pyres
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 mb-4 max-w-3xl mx-auto">
            Celebrate life with fire, music, and glory
          </p>
          <p className="text-lg text-slate-300 mb-12 max-w-2xl mx-auto">
            Leave the sadness behind. Embrace a legendary sendoff worthy of sagas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/create">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                Create Your Funeral
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="border-2 border-slate-300 text-white hover:bg-slate-800 text-lg px-8 py-6">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-slate-50 viking-pattern-section">
        <div className="viking-pattern-bg"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              A New Way to Say Goodbye
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Transform the funeral experience from somber to spectacular
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                  <Music className="w-8 h-8 text-amber-700" />
                </div>
                <CardTitle className="text-2xl">Epic Soundtrack</CardTitle>
                <CardDescription className="text-base">
                  Curate your perfect playlist - from Led Zeppelin to Viking war chants
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <MessageSquare className="w-8 h-8 text-blue-700" />
                </div>
                <CardTitle className="text-2xl">Quotes from Beyond</CardTitle>
                <CardDescription className="text-base">
                  Share your wisdom, humor, and final words to inspire those you leave behind
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <UtensilsCrossed className="w-8 h-8 text-green-700" />
                </div>
                <CardTitle className="text-2xl">Viking Feast</CardTitle>
                <CardDescription className="text-base">
                  Heineken on tap, roasted meats, and a celebration worthy of Valhalla
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-8 h-8 text-purple-700" />
                </div>
                <CardTitle className="text-2xl">Choose Your Scale</CardTitle>
                <CardDescription className="text-base">
                  Neighborhood, Tribe, or Country - select the size of your legendary sendoff
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-8 h-8 text-teal-700" />
                </div>
                <CardTitle className="text-2xl">Sacred Locations</CardTitle>
                <CardDescription className="text-base">
                  Find the perfect river or waterfall for your authentic funeral pyre
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <div className="w-14 h-14 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
                  <Share2 className="w-8 h-8 text-rose-700" />
                </div>
                <CardTitle className="text-2xl">Share Your Legacy</CardTitle>
                <CardDescription className="text-base">
                  Get a unique link to share your funeral plan with family and friends
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Attendance Levels Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Choose Your Attendance Level
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              From intimate gatherings to legendary celebrations
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-800 border-slate-700 hover:border-amber-500 transition-all duration-300">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <HomeIcon className="w-16 h-16 text-amber-500" />
                </div>
                <CardTitle className="text-3xl text-white">Neighborhood</CardTitle>
                <CardDescription className="text-slate-300 text-lg mt-2">
                  50-100 People
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-center mb-4">
                  An intimate gathering for close friends and family
                </p>
                <p className="text-4xl font-bold text-center text-amber-500">$2,500</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-2 border-amber-500 hover:shadow-2xl transition-all duration-300 transform scale-105">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Users className="w-16 h-16 text-amber-500" />
                </div>
                <CardTitle className="text-3xl text-white">Tribe</CardTitle>
                <CardDescription className="text-slate-300 text-lg mt-2">
                  100-300 People
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-center mb-4">
                  A community celebration with extended circles
                </p>
                <p className="text-4xl font-bold text-center text-amber-500">$7,500</p>
                <div className="mt-4 text-center">
                  <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-amber-500 transition-all duration-300">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Globe className="w-16 h-16 text-amber-500" />
                </div>
                <CardTitle className="text-3xl text-white">Country</CardTitle>
                <CardDescription className="text-slate-300 text-lg mt-2">
                  300+ People
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300 text-center mb-4">
                  An epic legendary sendoff worthy of sagas
                </p>
                <p className="text-4xl font-bold text-center text-amber-500">$15,000+</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Plan Your Legendary Sendoff?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Create your Viking funeral today and leave a legacy worth remembering
          </p>
          <Link to="/create">
            <Button size="lg" className="bg-white text-amber-700 hover:bg-slate-100 text-lg px-10 py-6 font-semibold">
              Start Planning Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;